import {createContext} from 'react';
import {getHomeActionName, getSectionAndPage} from './tracking';
import {HomeTabPage, ISecondTabs, ITabItem} from './type';
import {REGION} from '../../common/constant';
import {getThemeOnBody} from '../../common/function';
import {
  DEFAULT_LARGE_SIZE,
  HEADER_HEIGHT,
  MAIN_TAB_HEIGHT,
  SUB_TAB_HEIGHT,
} from '../../common/scss/util/variable';
import {getClientStore} from '../../common/store';
import {sendPageView} from '../../common/util/tracking';
import {TimeTableDay, TimeTableSecondTab} from '../timeTable/type';

const TAB_PAGE_ITEMS_KOR: ITabItem[] = [
  {tabPage: HomeTabPage.Ranking, title: t => t('common_tab_top')},
  {
    tabPage: HomeTabPage.TimeTable,
    title: t => t('common_tab_original_webtoon'),
  },
  {tabPage: HomeTabPage.MyPage, title: t => t('common_tab_library')},
];

const TAB_PAGE_ITEMS_GLOBAL: ITabItem[] = [
  {tabPage: HomeTabPage.Ranking, title: t => t('common_tab_top')},
  {
    tabPage: HomeTabPage.TimeTable,
    title: t => t('common_tab_time_table_global'),
  },
  {tabPage: HomeTabPage.MyPage, title: t => t('common_tab_library')},
];

export const TAB_PAGE_ITEMS =
  REGION === 'kor' ? TAB_PAGE_ITEMS_KOR : TAB_PAGE_ITEMS_GLOBAL;

export const DAYS_TAB_LIST: TimeTableDay[] = [
  'mon',
  'tue',
  'wed',
  'thu',
  'fri',
  'sat',
  'sun',
];
const TIME_TABLE_KEY_LIST: TimeTableSecondTab[] = [
  'new',
  ...DAYS_TAB_LIST,
  'complete',
];
const TIME_TABLE_TEXT_LIST: string[] = [
  'common_schedule_new',
  'common_schedule_mon',
  'common_schedule_tue',
  'common_schedule_wed',
  'common_schedule_thu',
  'common_schedule_fri',
  'common_schedule_sat',
  'common_schedule_sun',
  'common_schedule_completed',
];

export const INITIAL_SECOND_TABS: ISecondTabs = {
  [HomeTabPage.Ranking]: {
    selected: '', // 기본 값은 실시간 랭킹이지만 서버에서 받아오는 값으로 주입
    tabList: [],
  },
  [HomeTabPage.MyPage]: {
    selected: 'recent',
    tabList: [
      {
        key: 'recent',
        text: 'library_tab_recent',
      },
      {
        key: 'like',
        text: 'library_tab_favorites',
      },
      {
        key: 'own',
        text: 'library_tab_possessed',
      },
    ],
  },
  [HomeTabPage.TimeTable]: {
    selected: 'mon',
    tabList: TIME_TABLE_KEY_LIST.map((key, index) => ({
      key,
      text: TIME_TABLE_TEXT_LIST[index],
    })),
  },
};

// TODO: GNB 상태 값을 리덕스로 관리하자.
export const GNB_STATE: {
  headerEl: HTMLDivElement | null;
  prevScroll: number;
  isHidden: boolean;
} = {
  headerEl: null,
  prevScroll: 0,
  isHidden: false,
};
export function setHeaderIsHidden(isHidden: boolean) {
  if (!GNB_STATE.headerEl) return;
  requestAnimationFrame(() => {
    GNB_STATE.isHidden = isHidden;
    const isLarge =
      (GNB_STATE.headerEl?.clientWidth as number) >= DEFAULT_LARGE_SIZE;
    if (isHidden && !isLarge) {
      GNB_STATE.headerEl?.classList.add('-translate-y-header-height');
    } else {
      GNB_STATE.headerEl?.classList.remove('-translate-y-header-height');
    }
  });
}

export function onScrollGnb(
  scrollTop: number,
  onToggleGnb?: (isHide: boolean) => void
) {
  const {prevScroll, isHidden, headerEl} = GNB_STATE;
  if (!headerEl) return;
  if (!isHidden && scrollTop > prevScroll && scrollTop > 0) {
    setHeaderIsHidden(true);
    onToggleGnb?.(true);
  } else if (isHidden && (scrollTop <= 0 || prevScroll - scrollTop > 30)) {
    setHeaderIsHidden(false);
    onToggleGnb?.(false);
  }
  GNB_STATE.prevScroll = scrollTop;
}

export const DEFAULT_GENRE_KEY = 'timetable_completed';

export const HomePageContext = createContext<IHomePageContext>({
  isCurrentHomePage: false,
});
export interface IHomePageContext {
  isCurrentHomePage: boolean;
}

export function getDelta(subLabel: string): number | 'new' {
  return subLabel === 'NEW' ? 'new' : subLabel === '-' ? 0 : Number(subLabel);
}
export const NEED_TAB_QUERY_PAGE: {[key in HomeTabPage]: boolean} = {
  [HomeTabPage.Ranking]: false,
  [HomeTabPage.TimeTable]: true,
  [HomeTabPage.MyPage]: true,
};

export function getContentOrdNum(index: number, hasBigContent?: boolean) {
  const ordNum = index + 1;
  return hasBigContent ? ordNum + 1 : ordNum;
}

export function getGnbHeight(currentTab?: HomeTabPage) {
  const isTimeTableTab = currentTab === HomeTabPage.TimeTable;

  return isTimeTableTab
    ? HEADER_HEIGHT + MAIN_TAB_HEIGHT
    : HEADER_HEIGHT + MAIN_TAB_HEIGHT + SUB_TAB_HEIGHT;
}

export function getIsMainTab(currentTab: HomeTabPage) {
  const mainTab = HomeTabPage.TimeTable;

  return mainTab === currentTab;
}

export function getIsDayTab(secondTab?: string) {
  if (!secondTab) {
    return false;
  }

  return DAYS_TAB_LIST.includes(secondTab as TimeTableDay);
}

export function sendHomePageView({
  tabPage,
  secondTab,
  customProps,
}: {
  tabPage: HomeTabPage;
  secondTab: string;
  customProps?: object;
}) {
  const state = getClientStore()?.getState();
  if (!state) return;

  const actionName = getHomeActionName(tabPage, secondTab);
  const {section, page} = getSectionAndPage(state);

  if (actionName) {
    sendPageView({
      section,
      page,
      actionName,
      isSendExtra: tabPage === HomeTabPage.Ranking && secondTab !== 'hot',
      customProps: {
        display_theme: getThemeOnBody(),
        ...customProps,
      },
    });
  }
}
